
      import { createComponent } from '@lit/react'
      import { type EventName } from '@lit/react'
      import * as React from 'react'

      import Component from '../../components/file-input/file-input.component.js'
      import type { DdsUploadStartEvent } from '../../events/events'
import type { DdsUploadProgressEvent } from '../../events/events'
import type { DdsUploadCompleteEvent } from '../../events/events'
import type { DdsUploadErrorEvent } from '../../events/events'
import type { DdsAllUploadsCompleteEvent } from '../../events/events'
import type { DdsFileRemovedEvent } from '../../events/events'
import type { DdsFileDeleteErrorEvent } from '../../events/events'
import type { DdsFileChangeEvent } from '../../events/events'
      export type { DdsUploadStartEvent } from '../../events/events'
export type { DdsUploadProgressEvent } from '../../events/events'
export type { DdsUploadCompleteEvent } from '../../events/events'
export type { DdsUploadErrorEvent } from '../../events/events'
export type { DdsAllUploadsCompleteEvent } from '../../events/events'
export type { DdsFileRemovedEvent } from '../../events/events'
export type { DdsFileDeleteErrorEvent } from '../../events/events'
export type { DdsFileChangeEvent } from '../../events/events'

      const tagName = 'dap-ds-file-input'

      /**
 * `dap-ds-file-input`
 * @summary A file input is a control that allows users to select files to upload.
 * @element dap-ds-file-input
 * @title - File input
 *
 * @property {string} name - The name of the file input.
 * @property {string} value - The value of the file input.
 * @property {'success' | 'error'} status - The status of the file input.
 * @property {string} label - The label for the file input.
 * @property {string} description - The description for the file input.
 * @property {string} tooltip - The tooltip for the file input.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the input.
 * @property {boolean} disabled - Whether the file input is disabled.
 * @property { 'xs' | 'sm' | 'lg' } size - The size of the file input.
 * @property {string} feedback - The feedback for the file input.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The type of feedback for the file input.
 * @property {boolean} required - Whether the file input is required.
 * @property {boolean} optional - Whether the file input is optional.
 * @property {boolean} subtle - Font weight of the feedback label. Default is `false` which is bold.
 * @property {string} uploadButtonLabel - The label for the upload button.
 * @property {string} browseButtonLabel - The label for the browse button.
 * @property {string} accept - The file types that the file input accepts.
 * @property {boolean} multiple - Whether the file input accepts multiple files.
 * @property {boolean} keepValue - Whether to keep the value when new files are selected.
 * @property {string} uploadUrl - A remote URL to upload files to.
 * @property {string} deleteUrl - A remote URL to delete uploaded files from. Supports placeholders: `{fileName}`, `{id}`, `{key}`, `{location}`.
 * @property {string} uploadHeaders - Custom headers for upload requests (JSON string).
 * @property {string} deleteHeaders - Custom headers for delete requests (JSON string).
 * @property {boolean} autoupload - Whether to automatically upload files after selection.
 * @property {number} maxFiles - The maximum number of files that can be uploaded.
 * @property {number} maxFileSize - The maximum size of files that can be uploaded (in bytes).
 * @property {string} maxFileSizeErrorText - Text for the max file size error.
 * @property {string} maxFileAmountErrorText - Text for the max file amount error.
 * @property {string} fileTypeErrorText - Text for the file type error.
 * @property {boolean} showDropzone - Whether to show a separate dropzone area.
 * @property {string} dropzoneText - Text to display in the dropzone.
 * @property {boolean} showFileList - Whether to show the built-in file list.
 * @property {boolean} confirmRemove - Whether to show confirmation dialog before removing files.
 * @property {string} showUploadButton - Whether to show the upload button. Default is `true`.
 * @property {string} removeButtonLabel - The label for the remove button.
 * @property {string} cancelButtonLabel - The label for the cancel button.
 *
 * @event {{ file: File }} dds-upload-start - Fired when a file upload starts.
 * @event {{ file: File, progress: number }} dds-upload-progress - Fired during file upload progress.
 * @event {{ file: File, item: DapDSFileInputListItem, result: any }} dds-upload-complete - Fired when a file upload completes successfully.
 * @event {{ file: File, item: DapDSFileInputListItem, error: Error } | { errors: Error[] }} dds-upload-error - Fired when a file upload encounters an error. Can contain a single error or an array of validation errors.
 * @event {{ void }} dds-all-uploads-complete - Fired when all file uploads are complete.
 * @event {{ file: FileListElement }} dds-file-removed - Fired when a file is removed from the file input.
 * @event {{ file: FileListElement, error: Error }} dds-file-delete-error - Fired when a file deletion from server encounters an error.
 * @event {{ files: File[] }} dds-file-change - Fired when the file input value changes.
 *
 * @slot dropzone-content - The content to display in the dropzone.
 *
 * @csspart base - The main file input container.
 * @csspart input - The file input control.
 * @csspart label - The file input label.
 * @csspart description - The file input description.
 * @csspart feedback - The file input feedback.
 * @csspart tooltip - The file input tooltip.
 * @csspart upload-progress - The upload progress container.
 * @csspart browse-button - The browse button.
 * @csspart upload-button - The upload button.
 * @csspart dropzone - The dropzone area.
 * @csspart file-list - The file list container.
 * @csspart file-list-item - Individual file list items.
 */
      const reactWrapper = createComponent({
        tagName,
        elementClass: Component,
        react: React,
        events: {
          onDdsUploadStart: 'dds-upload-start' as EventName<DdsUploadStartEvent>,
onDdsUploadProgress: 'dds-upload-progress' as EventName<DdsUploadProgressEvent>,
onDdsUploadComplete: 'dds-upload-complete' as EventName<DdsUploadCompleteEvent>,
onDdsUploadError: 'dds-upload-error' as EventName<DdsUploadErrorEvent>,
onDdsAllUploadsComplete: 'dds-all-uploads-complete' as EventName<DdsAllUploadsCompleteEvent>,
onDdsFileRemoved: 'dds-file-removed' as EventName<DdsFileRemovedEvent>,
onDdsFileDeleteError: 'dds-file-delete-error' as EventName<DdsFileDeleteErrorEvent>,
onDdsFileChange: 'dds-file-change' as EventName<DdsFileChangeEvent>
        },
        displayName: "DapDSFileInput"
      })

      export default reactWrapper
    